import { useState } from 'react';
import { Invoice } from '@/data/mockData';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { formatCurrency } from '@/lib/utils';
import {
  Search,
  Eye,
  Edit,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  DollarSign,
  FileText,
  Shield,
  Receipt,
  Package,
  Truck
} from 'lucide-react';

// Define valid variants for Badge component
type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

interface InvoiceListProps {
  invoices: Invoice[];
  onView: (id: number) => void;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
  onApprove: (id: number) => void;
  onReject: (id: number) => void;
  onVerify: (id: number) => void;
  onDebitNotes?: (invoice: Invoice) => void;
  isLoading?: boolean;
}

export const InvoiceList = ({
  invoices,
  onView,
  onEdit,
  onDelete,
  onApprove,
  onReject,
  onVerify,
  onDebitNotes,
  isLoading = false,
}: InvoiceListProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');

  // Filter invoices
  const filteredInvoices = invoices.filter((invoice) => {
    const matchesSearch =
      invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.vendorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.poNumber.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
    const matchesPayment = paymentFilter === 'all' || invoice.paymentStatus === paymentFilter;

    return matchesSearch && matchesStatus && matchesPayment;
  });

  const getStatusVariant = (status: string): BadgeVariant => {
    switch (status) {
      case 'Approved':
      case 'Fully Received':
      case 'Verified':
      case 'Complete':
      case 'Fully Accepted':
        return 'default'; // Green/positive status
      case 'Rejected':
      case 'Fully Rejected':
        return 'destructive'; // Red/negative status
      case 'Pending':
      case 'Partially Received':
      case 'Partly Accepted':
        return 'secondary'; // Yellow/warning status
      case 'Draft':
      case 'Created':
        return 'outline'; // Grey/neutral status
      default:
        return 'outline';
    }
  };

  const getPaymentVariant = (status: string): BadgeVariant => {
    switch (status) {
      case 'Paid':
        return 'default';
      case 'Pending':
        return 'secondary';
      case 'Overdue':
        return 'destructive';
      case 'Partial':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Approved':
      case 'Fully Received':
      case 'Verified':
      case 'Complete':
      case 'Fully Accepted':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'Rejected':
      case 'Fully Rejected':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'Pending':
      case 'Partially Received':
      case 'Partly Accepted':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'Draft':
      case 'Created':
        return <FileText className="h-4 w-4 text-gray-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getPaymentIcon = (status: string) => {
    switch (status) {
      case 'Paid':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'Overdue':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'Pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'Partial':
        return <DollarSign className="h-4 w-4 text-blue-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const isOverdue = (invoice: Invoice) => {
    if (invoice.paymentStatus === 'Paid') return false;
    const dueDate = new Date(invoice.dueDate);
    const today = new Date();
    return dueDate < today;
  };


  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-4 bg-muted rounded w-1/4 mb-2"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search and Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search invoices by number, vendor, or PO..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-input bg-background rounded-md text-sm"
              >
                <option value="all">All Status</option>
                <option value="Created">Created</option>
                <option value="Draft">Draft</option>
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Verified">Verified</option>
                <option value="Rejected">Rejected</option>
                <option value="Fully Accepted">Fully Accepted</option>
                <option value="Partly Accepted">Partly Accepted</option>
                <option value="Complete">Complete</option>
              </select>
              <select
                value={paymentFilter}
                onChange={(e) => setPaymentFilter(e.target.value)}
                className="px-3 py-2 border border-input bg-background rounded-md text-sm"
              >
                <option value="all">All Payment</option>
                <option value="Pending">Pending</option>
                <option value="Paid">Paid</option>
                <option value="Partial">Partial</option>
                <option value="Overdue">Overdue</option>
              </select>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setPaymentFilter('all');
                }}
              >
                Clear
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoices List */}
      {filteredInvoices.length > 0 ? (
        <div className="space-y-4">
          {filteredInvoices.map((invoice) => (
            <Card key={invoice.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="font-semibold text-lg">{invoice.invoiceNumber}</h3>
                      <Badge variant={getStatusVariant(invoice.status)}>
                        <div className="flex items-center gap-1">
                          {getStatusIcon(invoice.status)}
                          {invoice.status}
                        </div>
                      </Badge>
                      <Badge variant={getPaymentVariant(invoice.paymentStatus)}>
                        <div className="flex items-center gap-1">
                          {getPaymentIcon(invoice.paymentStatus)}
                          {invoice.paymentStatus}
                        </div>
                      </Badge>
                      <Badge variant={invoice.isReceived ? 'default' : 'secondary'} className={
                        invoice.isReceived 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }>
                        <div className="flex items-center gap-1">
                          {invoice.isReceived ? (
                            <Package className="h-3 w-3" />
                          ) : (
                            <Truck className="h-3 w-3" />
                          )}
                          {invoice.deliveryStatus}
                        </div>
                      </Badge>
                      {isOverdue(invoice) && (
                        <Badge variant="destructive" className="text-xs">
                          Overdue
                        </Badge>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Vendor</p>
                        <p className="font-medium">{invoice.vendorName}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">PO Number</p>
                        <p className="font-medium">{invoice.poNumber}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Amount</p>
                        <p className="font-medium text-lg">{formatCurrency(invoice.totalAmount)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Due Date</p>
                        <p className="font-medium">{new Date(invoice.dueDate).toLocaleDateString()}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm mt-3">
                      <div>
                        <p className="text-muted-foreground">Invoice Date</p>
                        <p className="font-medium">{new Date(invoice.invoiceDate).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Payment Terms</p>
                        <p className="font-medium">{invoice.paymentTerms}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Items</p>
                        <p className="font-medium">{invoice.items.length} items</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Created</p>
                        <p className="font-medium">{new Date(invoice.createdAt).toLocaleDateString()}</p>
                      </div>
                    </div>

                    {invoice.notes && (
                      <div className="mt-3">
                        <p className="text-muted-foreground text-sm">Notes</p>
                        <p className="text-sm bg-muted/50 p-2 rounded">{invoice.notes}</p>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <Button variant="ghost" size="sm" onClick={() => onView(invoice.id)} title="View Details">
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => onEdit(invoice.id)} title="Edit Invoice">
                      <Edit className="h-4 w-4" />
                    </Button>
                    {invoice.status === 'Pending' && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-green-600 hover:text-green-700"
                          onClick={() => onApprove(invoice.id)}
                          title="Approve Invoice"
                        >
                          <CheckCircle className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => onReject(invoice.id)}
                          title="Reject Invoice"
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    {invoice.status === 'Approved' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-blue-600 hover:text-blue-700"
                        onClick={() => onVerify(invoice.id)}
                        title="Verify Invoice for IQC"
                      >
                        <Shield className="h-4 w-4" />
                      </Button>
                    )}
                    {(invoice.status === 'Rejected' || invoice.status === 'Fully Rejected') && onDebitNotes && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => onDebitNotes(invoice)}
                        title="Create Debit Notes"
                      >
                        <Receipt className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-blue-600 hover:text-blue-700"
                      title="Download Invoice"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-8 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center">
                <FileText className="h-8 w-8" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">No Invoices Found</h3>
                <p className="text-sm text-muted-foreground">
                  {searchTerm || statusFilter !== 'all' || paymentFilter !== 'all'
                    ? 'Try adjusting your search or filter criteria'
                    : 'No invoices have been created yet'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};